<template>
<!--for b-form checkbox
    @input Emitted when the selected value(s) is changed
    @change Emitted when selected value(s) is changed due to user interaction
-->
    <span class="p-checkbox-wrapper" :class="{ 'p-checkbox-inline': inline }">
        <span class="p-checkbox-body">
            <b-form-checkbox
                ref="bvCheckbox"
                :switch="useSwitch"
                v-bind="$attrs"
                :name="name"
                :model-value="isSelected"
                :class="{ 'compact-control' : compactFormat}"
                @change="$emit('change', $event)"
                @update:model-value="handleInput($event)"
                :indeterminate="indeterminate"
                >
            {{label}} <span v-if="infoMessage" class="ps-1" v-b-tooltip="{
                    title: infoMessage,
                    trigger: 'hover',
                    placement: 'auto',
                    boundary: 'viewport',
                    container: 'body',
                    teleportTo: 'body',
                    customClass: 'p-info-tooltip',
                    delay: { show: 0, hide: 100 }
                }">
                <b-icon-info-circle></b-icon-info-circle>
            </span>
            </b-form-checkbox>
        </span>
        <span v-if="$slots.append" class="p-checkbox-append ps-1">
            <slot name='append'></slot>
        </span>
    </span>
</template>
<script>

import baseInputs from '@/components/mixins/BaseInputs';

export default {
  name: "p-checkbox",
  emits: ['change', 'update:modelValue', 'input'],
  mixins: [baseInputs],
  props: {
    // value prop now comes from modelValue via BaseInputs mixin
    compactFormat: Boolean,
    useIndeterminate: {
      type: Boolean,
      default: false
    },
    useSwitch:{
      type: Boolean,
      default: false
    },
    checked: {
      type: Boolean,
      default: false
    },
    inline: {
      type: Boolean,
      default: false
    },
    infoMessage: String
  },
  watch: {
    checked(newVal) {
      // Emit input event to keep external value in sync
      this.$emit('update:modelValue', newVal);
      this.$emit('input', newVal);
    },
    // The checkbox "indeterminate" visual is a DOM property on the native
    // <input>, not an attribute — bootstrap-vue-next's one-way :indeterminate
    // binding doesn't reliably apply it (it's a v-model-style prop it resets
    // internally). Set the property directly and re-apply on any value change.
    indeterminate() {
      this.$nextTick(this.applyIndeterminate);
    },
    modelValue() {
      this.$nextTick(this.applyIndeterminate);
    }
  },
  mounted() {
    this.applyIndeterminate();
  },
  methods: {
    applyIndeterminate() {
      // The component root ($el) can be a non-Element (comment/text) node in
      // some render scenarios, so prefer the b-form-checkbox's own element and
      // guard that querySelector actually exists before calling it.
      const candidates = [this.$refs.bvCheckbox && this.$refs.bvCheckbox.$el, this.$el];
      for (const el of candidates) {
        if (el && typeof el.querySelector === 'function') {
          const input = el.querySelector('input[type="checkbox"]');
          if (input) {
            input.indeterminate = this.indeterminate;
            return;
          }
        }
      }
    }
  },
  computed: {
    value() { return this.modelValue; },
    isSelected() {
      return this.modelValue ?? this.checked ?? false;
    },
    indeterminate() {
        return this.useIndeterminate && this.value === null;
    }
  }
};
</script>
<style scoped lang="scss">
:deep(.compact-control ) {
    min-height: 20px;
}
.p-checkbox-wrapper {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-right: 0.5rem;
}
.p-checkbox-wrapper.p-checkbox-inline {
    display: inline-flex;
}
.p-checkbox-body {
    display: inline-flex;
    align-items: center;
}
.p-checkbox-append {
    display: inline-flex;
    align-items: center;
}

</style>

<!-- Unscoped: position the switch input absolutely at the form-check's left
     edge and pad the label past it with a fixed offset. Removes all the
     float/flex/negative-margin interactions that kept producing overlap. -->
<style lang="scss">
.p-checkbox-wrapper .form-check.form-switch {
    padding-left: 0 !important;
    position: relative !important;
    min-height: 1.5em !important;
}
.p-checkbox-wrapper .form-check.form-switch .form-check-input {
    margin: 0 !important;
    float: none !important;
    width: 2em !important;
    height: 1em !important;
    position: absolute !important;
    left: 0 !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
}
.p-checkbox-wrapper .form-check.form-switch .form-check-label {
    margin: 0 !important;
    padding-left: 2.5em !important;
    line-height: 1.5 !important;
    white-space: nowrap !important;
}
</style>



